import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tag, Trash2, Plus, Percent, PoundSterling, Check, X } from "lucide-react";

const EMPTY = { code: "", type: "percent", value: "", active: true, expires_at: "", max_uses: "", min_spend: "" };

export default function AdminDiscounts() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discounts");
      setCodes(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) {
      toast.error("Code and value are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        type: form.type,
        value: parseFloat(form.value),
        active: form.active,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        min_spend: form.min_spend ? parseFloat(form.min_spend) : null,
      };
      await api.post("/admin/discounts", payload);
      toast.success("Discount code created");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/discounts/${c.id}`, {
        code: c.code, type: c.type, value: c.value, active: !c.active,
        expires_at: c.expires_at, max_uses: c.max_uses, min_spend: c.min_spend,
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this discount code?")) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inputCls = "w-full bg-black/50 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[color:var(--ufr-green)] outline-none";
  const labelCls = "text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold mb-1.5 block";

  return (
    <div data-testid="admin-discounts">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Marketing</div>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Discount codes</h1>
      </div>

      <form onSubmit={submit} className="ufr-card rounded-md p-6 mb-8" data-testid="discount-form">
        <div className="flex items-center gap-2 mb-4">
          <Plus size={16} className="text-[color:var(--ufr-green)]" />
          <div className="text-sm font-bold text-white uppercase tracking-wider">New code</div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Code</label>
            <input className={inputCls} value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="discount-code-input" />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="discount-type-select">
              <option value="percent">Percentage (%)</option>
              <option value="fixed">Fixed amount (£)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{form.type === "percent" ? "Percent off" : "Amount off (£)"}</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder={form.type === "percent" ? "10" : "5.00"} data-testid="discount-value-input" />
          </div>
          <div>
            <label className={labelCls}>Expiry (optional)</label>
            <input className={inputCls} type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="discount-expiry-input" />
          </div>
          <div>
            <label className={labelCls}>Max uses (optional)</label>
            <input className={inputCls} type="number" min="1" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} placeholder="100" data-testid="discount-maxuses-input" />
          </div>
          <div>
            <label className={labelCls}>Min spend £ (optional)</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={form.min_spend} onChange={(e) => set("min_spend", e.target.value)} placeholder="10.00" data-testid="discount-minspend-input" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer" data-testid="discount-active-toggle">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[color:var(--ufr-green)] w-4 h-4" />
            Active
          </label>
          <button type="submit" disabled={saving} className="btn-ufr-primary px-6 py-3 text-sm" data-testid="discount-create-btn">
            {saving ? "Saving…" : "Create code"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center">
          <Tag className="mx-auto text-white/30" size={32} />
          <div className="font-display text-2xl text-white mt-3">No discount codes yet</div>
          <p className="text-white/60 text-sm mt-2">Create your first code above to reward your customers.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="discount-list">
          {codes.map((c) => (
            <div key={c.id} className="ufr-card rounded-md p-4 flex items-center gap-4 flex-wrap" data-testid={`discount-row-${c.code}`}>
              <div className="flex items-center gap-2">
                {c.type === "percent" ? <Percent size={16} className="text-[color:var(--ufr-green)]" /> : <PoundSterling size={16} className="text-[color:var(--ufr-green)]" />}
                <span className="font-display text-xl text-white tracking-wide">{c.code}</span>
              </div>
              <div className="text-sm text-[color:var(--ufr-green)] font-bold">
                {c.type === "percent" ? `${c.value}% off` : `£${Number(c.value).toFixed(2)} off`}
              </div>
              <div className="text-xs text-white/50 flex flex-wrap gap-3">
                {c.min_spend ? <span>min £{Number(c.min_spend).toFixed(2)}</span> : null}
                {c.max_uses ? <span>{c.used_count || 0}/{c.max_uses} used</span> : <span>{c.used_count || 0} used</span>}
                {c.expires_at ? <span>expires {new Date(c.expires_at).toLocaleDateString()}</span> : null}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => toggleActive(c)} className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 ${c.active ? "bg-[rgba(34,255,102,0.12)] text-[color:var(--ufr-green)]" : "bg-white/5 text-white/50"}`} data-testid={`discount-toggle-${c.code}`}>
                  {c.active ? <><Check size={12} /> Active</> : <><X size={12} /> Inactive</>}
                </button>
                <button onClick={() => remove(c.id)} className="btn-ufr-ghost px-3 py-2 text-[11px] hover:!border-red-500 hover:!text-red-400" data-testid={`discount-delete-${c.code}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
